// features/admin/reviewApi.ts

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// API Configuration
const API_CONFIG = {
  baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
};

// API Endpoints
const ENDPOINTS = {
  REVIEW_LIST: 'user/listReviewsProducts',
  REVIEW_ADD: 'user/addReview',
  REVIEW_UPDATE: 'user/updateReviewStatus',
  REVIEW_DELETE: 'user/deleteReview',
  REVIEW_COUNT: 'user/reviewCount',
};

// Types
export interface Review {
  id: number;
  review_id?: number;
  rating: number;
  comment: string;
  created_date: string;
  customer_name: string;
  product_id: number;
  seller_id: number | null;
  status: number;
  type?: number;
  media_path?: string;
  media_type?: string;
}

export interface ApiResponse<T> {
  error: boolean;
  message: string;
  data?: T;
  result?: T;
  count?: number;
}

export interface ReviewListParams {
  start?: number;
  limit?: number;
  search_str?: string;
  status?: number;
  product_id?: number;
  seller_id?: number;
  type?: number;
}

export interface ReviewCountParams {
  status?: number;
  type?: number;
  product_id?: number;
  seller_id?: number;
  search_str?: string;
}

export interface ReviewDeleteParams {
  review_id: number;
}

export interface AddReviewRequest {
  product_id: number;
  seller_id: number | null;
  rating: number;
  comment: string;
  type?: number;
}

export interface UpdateReviewRequest {
  review_id: number;
  status: number;
}

export interface ReviewFormData {
  product_id: number;
  seller_id: number | null;
  rating: number;
  comment: string;
  status: number;
  type?: number;
}

export interface FormErrors {
  [key: string]: string;
}

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// Create the API
export const reviewApi = createApi({
  reducerPath: 'reviewApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_CONFIG.baseUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      
      headers.set('authkey', API_CONFIG.AUTH_KEY);
      headers.set('Content-Type', 'application/json');
      
      return headers;
    },
  }),
  tagTypes: ['Review', 'ReviewCount', 'ReviewList'],
  endpoints: (build) => ({
    // 1. List Reviews with pagination and filters
    listReviews: build.query<ApiResponse<Review[]>, ReviewListParams>({
      query: (params = {}) => {
        const { start = 0, limit = 10, search_str, status, product_id, seller_id, type } = params;
        
        const body: any = {
          start,
          limit,
        };

        if (search_str?.trim()) {
          body.search_str = search_str.trim();
        }
        
        if (status !== undefined) {
          body.status = status;
        }
        
        if (product_id && product_id > 0) {
          body.product_id = product_id;
        }
        
        if (seller_id && seller_id > 0) {
          body.seller_id = seller_id;
        }
        
        if (type !== undefined) {
          body.type = type;
        }

        console.log('List Reviews Request Body:', body);

        return {
          url: ENDPOINTS.REVIEW_LIST,
          method: 'POST',
          body: JSON.stringify(body),
        };
      },
      providesTags: (result) => {
        const tags: any[] = ['ReviewList'];
        
        if (result?.result) {
          result.result.forEach((review) => {
            tags.push({ type: 'Review', id: review.id });
          });
        }
        
        return tags;
      },
      transformResponse: (response: any) => {
        const reviews = (response.result || response.data || []).map((review: any) => ({
          ...review,
          id: review.id || review.review_id || 0,
          review_id: review.id || review.review_id || 0,
        }));

        return {
          error: response.error || false,
          message: response.message || '',
          result: reviews,
          count: response.count || reviews.length || 0,
        };
      },
    }),

    // 2. Add Review
    addReview: build.mutation<ApiResponse<any>, AddReviewRequest>({
      query: (data) => {
        console.log('Add Review Request:', data);
        return { 
          url: ENDPOINTS.REVIEW_ADD,
          method: 'POST',
          body: JSON.stringify(data),
        };
      },
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList'],
    }),

    // 3. Update Review Status
    updateReview: build.mutation<ApiResponse<any>, UpdateReviewRequest>({
      query: ({ review_id, status }) => {
        console.log('Update Review Request:', { review_id, status });
        return {
          url: ENDPOINTS.REVIEW_UPDATE,
          method: 'POST',
          body: JSON.stringify({ 
            review_id,
            status 
          }),
        };
      },
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList'],
    }),

    // 4. Delete Review
    deleteReview: build.mutation<ApiResponse<any>, ReviewDeleteParams>({
      query: ({ review_id }) => {
        console.log('Delete Review Request:', { review_id });
        return {
          url: ENDPOINTS.REVIEW_DELETE,
          method: 'POST',
          body: JSON.stringify({ review_id }),
        };
      },
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList'],
    }),

    // 5. Get Review Count with filters
    getReviewCount: build.query<ApiResponse<{ count: number }>, ReviewCountParams>({
      query: (params = {}) => {
        const { status, type, product_id, seller_id, search_str } = params;
        
        const body: any = {};

        if (status !== undefined) {
          body.status = status;
        }
        
        if (type !== undefined) {
          body.type = type;
        }
        
        if (product_id && product_id > 0) {
          body.product_id = product_id;
        }
        
        if (seller_id && seller_id > 0) {
          body.seller_id = seller_id;
        }
        
        if (search_str?.trim()) {
          body.search_str = search_str.trim();
        }

        console.log('Review Count Request Body:', body);

        return {
          url: ENDPOINTS.REVIEW_COUNT,
          method: 'POST',
          body: JSON.stringify(body),
        };
      },
      providesTags: ['ReviewCount'],
      transformResponse: (response: any) => {
        console.log('Review Count Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          count: response.count || 0,
        };
      },
    }),
  }),
});

export const {
  useListReviewsQuery,
  useAddReviewMutation,
  useUpdateReviewMutation,
  useDeleteReviewMutation,
  useGetReviewCountQuery,
} = reviewApi;